import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Activity, 
  PlusCircle, 
  Trash2, 
  RefreshCw, 
  User, 
  Clock,
  Shield,
  Users
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { AuditLogEntry } from '@/types';

interface ActivityFeedProps {
  logs: AuditLogEntry[];
  currentHouseholdId: string | null;
  activeScope: 'PERSONAL' | 'SHARED' | 'BOTH';
}

/**
 * 📡 ActivityFeed Widget
 * Refactored to distinguish between Personal Partition and Shared Hub operations.
 */
export const ActivityFeed: React.FC<ActivityFeedProps> = ({ 
  logs, 
  currentHouseholdId,
  activeScope 
}) => {
  const { t } = useTranslation();

  // 🛡️ HYBRID FILTERING: Ensure the feed respects the user's selected scope
  const displayLogs = [...(logs || [])]
    .filter(log => {
      if (!currentHouseholdId) return log.scope === 'personal';
      if (activeScope === 'PERSONAL') return log.scope === 'personal';
      if (activeScope === 'SHARED') return log.scope === 'household';
      return true; // 'BOTH'
    })
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, 8);

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'CREATE': return <PlusCircle className="w-3 h-3 text-brand" />;
      case 'DELETE': return <Trash2 className="w-3 h-3 text-rose-500" />;
      case 'UPDATE': return <RefreshCw className="w-3 h-3 text-amber-500" />;
      case 'checkin': return <User className="w-3 h-3 text-emerald-500" />;
      case 'geofence_arrive': return <User className="w-3 h-3 text-green-500" />;
      case 'geofence_depart': return <Clock className="w-3 h-3 text-blue-400" />;
      default: return <Activity className="w-3 h-3 text-slate-400" />;
    }
  };

  const getLogColor = (log: AuditLogEntry) => {
    // 🎨 Personal logs get an emerald tint to signify privacy
    if (log.scope === 'personal') return 'bg-emerald-500/5 border-emerald-100 dark:border-emerald-900/20';

    switch ((log.actionType ?? log.action) as string) {
      case 'CREATE': return 'bg-indigo-500/10 border-indigo-100 dark:border-indigo-900/30';
      case 'DELETE': return 'bg-rose-500/10 border-rose-100 dark:border-rose-900/30';
      case 'UPDATE': return 'bg-amber-500/10 border-amber-100 dark:border-amber-900/30';
      case 'checkin': return 'bg-emerald-500/10 border-emerald-100 dark:border-emerald-900/30';
      case 'geofence_arrive': return 'bg-green-500/10 border-green-100 dark:border-green-900/30';
      case 'geofence_depart': return 'bg-blue-500/10 border-blue-100 dark:border-blue-900/30';
      default: return 'bg-slate-50 dark:bg-slate-800 border-slate-100 dark:border-slate-800';
    }
  };

  return (
    <div className="flex flex-col h-full space-y-4">
      {/* --- WIDGET HEADER --- */}
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-slate-400" />
          <h3 className="text-2xs font-black uppercase tracking-widest text-slate-500">
            {currentHouseholdId ? t('dash.activity.pulse', 'Telemetry Stream') : t('dash.activity.personal', 'Personal Logs')}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {/* 🛡️ Privacy Badge */}
          <div className="flex items-center gap-1 px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded-full border border-slate-200 dark:border-slate-700">
            {activeScope === 'PERSONAL' || !currentHouseholdId ? (
              <Shield className="w-2 h-2 text-emerald-500" />
            ) : (
              <Users className="w-2 h-2 text-brand" />
            )}
            <span className="text-[7px] font-black text-slate-400 uppercase">
              {currentHouseholdId ? activeScope : 'Personal'}
            </span>
          </div>
          <span className="text-[8px] font-black text-emerald-500 uppercase flex items-center gap-1">
            <span className="w-1 h-1 bg-emerald-500 rounded-full animate-ping" />
            {t('dash.activity.live', 'Live')}
          </span>
        </div>
      </div>

      {/* --- LOG STREAM --- */}
      <div className="flex-1 overflow-y-auto pr-1 space-y-3 no-scrollbar">
        {displayLogs.length > 0 ? (
          displayLogs.map((log) => (
            <div 
              key={log.id} 
              className={`flex items-start gap-3 p-3 rounded-2xl border transition-all hover:shadow-sm ${getLogColor(log)}`}
            >
              <div className="mt-0.5 p-1.5 bg-white dark:bg-slate-900 rounded-lg shadow-sm">
                {getActionIcon(log.actionType ?? log.action ?? '')}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-start gap-2">
                  <p className="text-2xs font-bold text-slate-800 dark:text-slate-100 leading-tight">
                    <span className={`font-black ${log.scope === 'personal' ? 'text-emerald-600 dark:text-emerald-400' : 'text-brand dark:text-indigo-400'}`}>
                      {log.scope === 'personal' ? t('common.you', 'You') : log.userName}
                    </span>
                    <span className="text-slate-400 font-medium"> {(log.actionType ?? log.action ?? '').toLowerCase()}d </span>
                    <span className="italic">"{log.taskTitle ?? log.entityName}"</span>
                  </p>
                </div>
                
                <div className="flex items-center justify-between mt-1 opacity-50">
                  <div className="flex items-center gap-2">
                    <Clock className="w-2.5 h-2.5 text-slate-400" />
                    <span className="text-[8px] font-black uppercase tracking-tighter text-slate-500">
                      {formatDistanceToNow(log.timestamp, { addSuffix: true })}
                    </span>
                  </div>
                  {/* Scope Label */}
                  <span className={`text-[6px] font-black uppercase tracking-tighter px-1.5 py-0.5 rounded border ${
                    log.scope === 'personal' ? 'text-emerald-500 border-emerald-500/20' : 'text-brand border-brand/20'
                  }`}>
                    {log.scope}
                  </span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="h-full flex flex-col items-center justify-center py-10 opacity-20 text-center space-y-2">
            <Activity className="w-10 h-10" />
            <p className="text-2xs font-black uppercase tracking-widest">No Recent Operations</p>
          </div>
        )}
      </div>

      {/* --- SYSTEM FOOTER --- */}
      <div className="pt-2 flex items-center gap-2">
        <div className="flex-1 h-px bg-slate-100 dark:bg-slate-800" />
        <p className="text-[7px] font-black text-slate-300 uppercase tracking-[0.3em]">
          End of Stream
        </p>
        <div className="flex-1 h-px bg-slate-100 dark:bg-slate-800" />
      </div>
    </div>
  );
};